import { useTranslation } from 'react-i18next';
import { resolveHandleCountry } from 'src/functions/phone.functions';
import { countryLabel } from '../Wallet.country';
import { isTemplateFreeAtSource } from './useWalletSendGate';
import { useWalletSurfaceAccess } from './useWalletSurfaceAccess';

/**
 * El chip «Con costo» que rotula la columna de precios de la lista de
 * plantillas (RET-2232, encargo del 29-sep).
 *
 * POR QUÉ EXISTE: RET-2106 convirtió el precio de cada renglón en una columna
 * y le quitó el prefijo «Con costo · ». Sin un rótulo, la lista ya no dice en
 * ningún lado que esos números cuestan. El prototipo (pantalla 10) resuelve
 * eso con el MISMO sello del compositor puesto en la línea de pestañas: mismo
 * componente, mismas dos palabras, no un texto parecido.
 *
 * LO ÚNICO QUE CAMBIA ES EL TOOLTIP, porque cambia la pregunta. En el
 * compositor hay UN precio y se dice cuál. En una lista hay varios, y lo que
 * hace falta es qué los diferencia: el tipo de plantilla y el país de quien la
 * recibe.
 */
export type tTemplateListPaidChip = {
  /** El texto del hover, ya unido. Nunca vacío cuando el chip se pinta. */
  tooltip: string;
  /**
   * Las oraciones por separado: la primera siempre, la segunda sólo cuando el
   * destinatario se pudo nombrar.
   *
   * Existe para que se pueda AFIRMAR sobre ellas. La clave de la primera
   * (`…paidChip.tooltip`) es PREFIJO de la de la segunda
   * (`…paidChip.tooltipRecipient`), y el doble global de `react-i18next`
   * devuelve la clave cruda: sobre la cadena unida, `toContain` y
   * `startsWith` dan verdadero para las dos, así que una vara escrita sobre
   * `tooltip` no puede distinguir «están las dos oraciones» de «está sólo la
   * segunda» — un centinela de subcadena. Sobre el arreglo sí.
   */
  sentences: string[];
};

export function useTemplateListPaidChip(params: {
  /** Los `template_type` de los renglones VISIBLES, ya filtrados. */
  templateTypes: readonly (string | undefined)[];
  /** El teléfono del destinatario, para resolver su país. */
  recipientHandle: string | undefined;
  /** El nombre del lead, para nombrarlo en la segunda oración. */
  recipientName: string | undefined;
  /**
   * Los dos hechos del chat que deciden la gratuidad, como PARÁMETROS y no
   * leídos de `useChatContext` aquí — igual que `useComposerServiceSeal`, que
   * recibe su `selectedChat`.
   *
   * Es capa, no comodidad: el slice del wallet no importa el contexto del
   * chat. Importarlo arrastraría `Chat.actions` y con él el singleton de
   * Firebase a cada arnés que monte la lista de plantillas, que es
   * exactamente lo que rompió `WabTemplatesAction.utilityEmpty.test.tsx` en
   * la primera versión de este hook.
   */
  isChatActive: boolean;
  hasFreeEntryPoint: boolean;
  /**
   * El modo AGENDAR de la barra de mensajes.
   *
   * El encargo pedía derivar la condición de `badgeClaimsFree` «si ya entró
   * el corte fechado», y ya entró: `Template.component.tsx` calcula
   * `badgeClaimsFree = isFreeAtSource && !isSchedulePickerMode`. En modo
   * agendar TODOS los renglones retiran «Gratis» (un envío programado
   * dispara en el futuro y no puede prometer gratuidad), así que sin este
   * dato el chip NO aparecía justo en el modo donde la columna se queda sin
   * rótulo — el estado que este chip existe para rotular.
   *
   * Es de nivel barra, no por renglón, por eso viaja aparte del predicado.
   */
  isSchedulePickerMode: boolean;
}): tTemplateListPaidChip | null {
  const { t, i18n } = useTranslation('wallet');
  const { isWalletSurfaceVisible } = useWalletSurfaceAccess();

  /*
    Con la bandera apagada o sin resolver no se pinta nada de wallet, igual
    que los precios de la columna que este chip rotula.

    RET-2339 (U4): el chip es una decisión de VISIBILIDAD, así que lee la
    misma que las demás superficies del wallet: `isWalletSurfaceVisible`
    (bandera cargada para ESTE workspace y encendida, plan resuelto y con
    WhatsApp API). Antes leía sólo la bandera, y desde el barrido del 30-sep
    la tiene casi toda la base: un Básico veía «Con costo» sobre un saldo que
    su plan no puede gastar. La parte de la bandera sigue llaveada por
    workspace (`hasLoadedWalletFlag`, nunca `isLoadingWalletFlag`), porque
    `isWalletSurfaceVisible` ya la exige. Costo aceptado: un Pro no ve el
    chip mientras su plan carga.
  */
  if (!isWalletSurfaceVisible) return null;

  /*
    LA CONDICIÓN: sólo si NINGÚN renglón visible dice «Gratis» (decisión de
    Fanny, 29-sep).

    En una conversación activa hay renglones gratis —una utility dentro de la
    ventana de 24 h hasta el corte, cualquiera con la ventana del anuncio abierta—
    y un «Con costo» encima los contradice. El prototipo sólo dibuja la lista
    en la conversación inactiva, donde todo cuesta y el chip se ve siempre.

    Se deriva del MISMO predicado por renglón que pinta «Gratis» en la columna
    (`isTemplateFreeAtSource`, el de RET-2234), con las MISMAS entradas que usa
    `Template.component.tsx`. La lectura ESTRICTA del estado del chat (D-5) la
    hace el llamador al armar `isChatActive`. Una segunda copia del PREDICADO
    es exactamente cómo el rótulo y los renglones acabarían diciendo cosas
    distintas el día del corte.
  */
  const { isChatActive, hasFreeEntryPoint } = params;

  const someRowClaimsFree =
    !params.isSchedulePickerMode &&
    params.templateTypes.some((templateType) =>
      isTemplateFreeAtSource({
        templateType: templateType ?? '',
        isChatActive,
        hasFreeEntryPoint,
      }),
    );

  if (someRowClaimsFree) return null;

  // Una lista vacía no tiene columna de precios que rotular.
  if (params.templateTypes.length === 0) return null;

  const sentences = buildSentences();

  return { tooltip: sentences.join(' '), sentences };

  function buildSentences(): string[] {
    const base = t('templateList.paidChip.tooltip');
    const country = resolveHandleCountry(params.recipientHandle);

    // Sin país resuelto va sólo la primera oración, que es cierta siempre. El
    // estimador no devuelve el país, así que esto sale del número; cuando no
    // se puede afirmar, no se inventa.
    if (!country || !params.recipientName) return [base];

    const countryName = countryLabel(country.iso2, i18n.language);
    if (!countryName) return [base];

    return [
      base,
      t('templateList.paidChip.tooltipRecipient', {
        name: params.recipientName,
        country: countryName,
        callingCode: country.callingCode,
      }),
    ];
  }
}
